function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { Code, ConnectError } from '@connectrpc/connect';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { pricingApiConnect } from '../../../../lib/api/pricingApi.connect';
import { projectDataClient } from '../../../../lib/connect/clients';
import { applyPricingOps, pricingDiff } from './pricingOps';
/** A session outlives its mounted editor: navigation never drops a queued or failed write. */ export class PricingAutosaveSession {
    emit(next) {
        this.snapshot = {
            ...this.snapshot,
            ...next
        };
        for (const listener of this.listeners)listener();
    }
    receive(pricing) {
        if (this.pending.size || this.batch || this.writing || [
            'conflict',
            'retry',
            'error'
        ].includes(this.snapshot.status)) return;
        this.stamp = pricing.updatedAt;
        this.emit({
            draft: pricing
        });
    }
    edit(next) {
        if (!this.snapshot.draft) return;
        const ops = pricingDiff(this.snapshot.draft, next);
        if (!ops.length) return;
        if (this.snapshot.status === 'saved') this.retried = false;
        for (const op of ops){
            // Container replacements subsume queued children, using the latest full local value.
            for (const path of this.pending.keys())if (path.startsWith(`${op.path}/`)) this.pending.delete(path);
            const parent = [
                ...this.pending.keys()
            ].find((path)=>op.path.startsWith(`${path}/`));
            if (parent) {
                const value = parent.slice(1).split('/').reduce((node, token)=>node[token.replace(/~1/g, '/').replace(/~0/g, '~')], next);
                this.pending.set(parent, {
                    op: {
                        op: 'set',
                        path: parent,
                        valueJson: JSON.stringify(value)
                    },
                    due: Date.now() + 800
                });
            } else this.pending.set(op.path, {
                op,
                due: Date.now() + 800
            });
        }
        this.emit({
            draft: next,
            status: [
                'conflict',
                'retry',
                'error'
            ].includes(this.snapshot.status) ? this.snapshot.status : 'pending'
        });
        this.schedule();
    }
    schedule() {
        clearTimeout(this.timer);
        if (this.writing || this.batch || !this.pending.size || [
            'conflict',
            'retry',
            'error'
        ].includes(this.snapshot.status)) return;
        const delay = Math.max(0, Math.min(...[
            ...this.pending.values()
        ].map((p)=>p.due)) - Date.now());
        this.timer = setTimeout(()=>{
            if (this.frame !== undefined) return;
            this.frame = requestAnimationFrame(()=>{
                this.frame = undefined;
                void this.flush();
            });
        }, delay);
    }
    async flush() {
        if (this.writing || [
            'conflict',
            'retry',
            'error'
        ].includes(this.snapshot.status)) return;
        if (!this.batch) {
            const ready = [
                ...this.pending.values()
            ].filter((p)=>p.due <= Date.now()).slice(0, 50);
            if (!ready.length) {
                this.schedule();
                return;
            }
            for (const { op } of ready)this.pending.delete(op.path);
            this.batch = {
                ops: ready.map((p)=>p.op),
                clientBatchId: crypto.randomUUID(),
                expectedUpdatedAt: this.stamp
            };
        }
        this.writing = true;
        this.emit({
            status: 'pending',
            error: undefined
        });
        try {
            const response = await this.transport.write(this.batch);
            this.stamp = response.scenarioUpdatedAt;
            this.batch = undefined;
            // Refresh failures must never cause a successful write to be submitted twice.
            await this.transport.refresh();
            this.emit({
                status: this.pending.size ? 'pending' : 'saved'
            });
        } catch (error) {
            const conflict = error instanceof ConnectError && [
                Code.Aborted,
                Code.FailedPrecondition
            ].includes(error.code);
            this.emit({
                status: conflict ? 'conflict' : 'error',
                error: error instanceof Error ? error.message : String(error)
            });
        } finally{
            this.writing = false;
            this.schedule();
        }
    }
    get canRetryConflict() {
        return !this.retried;
    }
    constructor(transport){
        _define_property(this, "transport", void 0);
        _define_property(this, "snapshot", void 0);
        _define_property(this, "listeners", void 0);
        _define_property(this, "pending", void 0);
        _define_property(this, "timer", void 0);
        _define_property(this, "frame", void 0);
        _define_property(this, "batch", void 0);
        _define_property(this, "writing", void 0);
        _define_property(this, "stamp", void 0);
        _define_property(this, "retried", void 0);
        _define_property(this, "reloaded", void 0);
        _define_property(this, "getSnapshot", void 0);
        _define_property(this, "subscribe", void 0);
        _define_property(this, "reload", void 0);
        _define_property(this, "retry", void 0);
        this.transport = transport;
        this.snapshot = {
            status: 'idle'
        };
        this.listeners = new Set();
        this.pending = new Map();
        this.writing = false;
        this.stamp = '';
        this.retried = false;
        this.getSnapshot = ()=>this.snapshot;
        this.subscribe = (listener)=>{
            this.listeners.add(listener);
            return ()=>{
                this.listeners.delete(listener);
            };
        };
        this.reload = async ()=>{
            if (this.writing || this.snapshot.status !== 'conflict') return;
            try {
                const fresh = await this.transport.reload();
                this.stamp = fresh.updatedAt;
                this.reloaded = fresh;
                this.emit({
                    draft: fresh,
                    status: 'retry',
                    error: undefined
                });
            } catch (error) {
                this.emit({
                    error: String(error)
                });
            }
        };
        this.retry = ()=>{
            if (this.writing) return;
            if (this.snapshot.status === 'retry') {
                if (this.retried || !this.reloaded) return;
                this.retried = true;
                const ops = [
                    ...this.batch?.ops ?? [],
                    ...[
                        ...this.pending.values()
                    ].map((p)=>p.op)
                ];
                const draft = applyPricingOps(this.reloaded, ops);
                // Rebase only explicit local ops onto the freshly loaded server document.
                this.batch = undefined;
                this.pending.clear();
                this.emit({
                    draft: this.reloaded,
                    status: 'saved'
                });
                this.edit(draft);
                this.retried = true;
            } else if (this.snapshot.status === 'error') {
                this.emit({
                    status: 'pending',
                    error: undefined
                });
                if (this.batch) void this.flush();
                else {
                    this.writing = true;
                    void this.transport.refresh().then(()=>{
                        this.emit({
                            status: this.pending.size ? 'pending' : 'saved'
                        });
                    }).catch((error)=>this.emit({
                            status: 'error',
                            error: String(error)
                        })).finally(()=>{
                        this.writing = false;
                        this.schedule();
                    });
                }
            }
        };
    }
}
const sessions = new WeakMap();
export function usePricingAutosave(scope, pricing) {
    const client = useQueryClient();
    let cache = sessions.get(client);
    if (!cache) {
        cache = new Map();
        sessions.set(client, cache);
    }
    const key = JSON.stringify([
        scope.principal,
        scope.projectId,
        scope.scenarioId
    ]);
    let session = cache.get(key);
    if (!session) {
        session = new PricingAutosaveSession({
            write: (batch)=>projectDataClient.patchPricingScenario({
                    projectId: scope.projectId,
                    scenarioId: scope.scenarioId,
                    ...batch
                }),
            reload: async ()=>(await pricingApiConnect.getPricing(scope.projectId, scope.scenarioId)).pricing,
            refresh: ()=>Promise.all([
                    'pricing-compare',
                    'pricing',
                    'pricing-totals',
                    'pricing-detail',
                    'pricing-ratebook',
                    'pricing-ratebook-row',
                    'pricing-evidence',
                    'projectActivity'
                ].map((name)=>client.invalidateQueries({
                        queryKey: [
                            name
                        ]
                    })))
        });
        cache.set(key, session);
    }
    const current = session;
    const snapshot = useSyncExternalStore(current.subscribe, current.getSnapshot);
    useEffect(()=>{
        if (pricing) current.receive(pricing);
    }, [
        current,
        pricing
    ]);
    useEffect(()=>{
        const unload = (event)=>{
            const dirty = [
                ...sessions.get(client)?.values() ?? []
            ].some((s)=>![
                    'idle',
                    'saved'
                ].includes(s.getSnapshot().status));
            if (dirty) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', unload);
        return ()=>window.removeEventListener('beforeunload', unload);
    }, [
        client
    ]);
    return {
        ...snapshot,
        edit: (next)=>current.edit(next),
        reload: current.reload,
        retry: current.retry,
        canRetryConflict: current.canRetryConflict
    };
}
